/**
 * BanksAdminPage — STAS multi-bank manager.
 *
 * Lists every bank account (الراجحي / الإنماء / STC BANK / …) and lets STAS
 * add / edit / toggle (enabled/disabled) / delete. Enabled banks show up in
 * the client's bank-picker on PortalTrack.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Plus, Pencil, Trash2, Power, PowerOff, X, Save,
  Building2, AlertCircle, CheckCircle2, Upload,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/** Absolute URL for a stored logo. Backend writes relative paths like
 *  `/api/portal/banks/<id>/logo?v=…`. We prepend REACT_APP_BACKEND_URL when
 *  needed. External http(s) URLs (legacy) pass through. */
function resolveLogo(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${BACKEND_URL}${url}`;
}

const EMPTY = {
  bank_name: "", bank_code: "", account_name: "", account_number: "", iban: "",
  swift: "", logo_url: "", instructions_ar: "", instructions_en: "",
  is_enabled: true, sort_order: 0,
};

export default function BanksAdminPage() {
  const { user } = useAuth();
  const { theme, dir } = useI18n();
  const navigate = useNavigate();
  const [banks, setBanks] = useState([]);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | bank
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [logoFile, setLogoFile] = useState(null);    // file picked but not yet uploaded
  const [logoPreview, setLogoPreview] = useState(""); // dataURL for live preview
  const BackIcon = dir === "rtl" ? ArrowRight : ArrowLeft;
  const css = makeStyles(theme);

  const load = async () => {
    try { setErr(""); const r = await api.get("/admin/banks"); setBanks(r.data.items || []); }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { if (user?.role?.is_root) load(); }, [user]);

  if (!user?.role?.is_root) {
    return <div style={css.page}><div style={css.err}>هذه الصفحة لـ STAS فقط</div></div>;
  }

  const openNew = () => { setForm({ ...EMPTY, sort_order: banks.length + 1 }); setLogoFile(null); setLogoPreview(""); setEditing("new"); };
  const openEdit = (b) => { setForm({ ...EMPTY, ...b }); setLogoFile(null); setLogoPreview(""); setEditing(b); };
  const close = () => { setEditing(null); setForm(EMPTY); setLogoFile(null); setLogoPreview(""); };

  const onPickLogo = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setErr("حجم الشعار أكبر من 2 ميجا"); return; }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setErr("");
    try {
      const payload = { ...form, sort_order: Number(form.sort_order) || 0 };
      let savedId = editing?.id;
      if (editing === "new") {
        const r = await api.post("/admin/banks", payload);
        savedId = r.data?.id;
      } else {
        await api.patch(`/admin/banks/${editing.id}`, payload);
      }
      // If the STAS picked a new file, upload it after the bank row exists.
      if (logoFile && savedId) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await api.post(`/admin/banks/${savedId}/logo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      await load();
      close();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const removeLogo = async () => {
    if (!editing || editing === "new") return;
    if (!window.confirm("حذف شعار البنك؟")) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/admin/banks/${editing.id}/logo`);
      setForm((s) => ({ ...s, logo_url: "" }));
      setLogoFile(null);
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggle = async (b) => {
    setBusy(true);
    try { await api.post(`/admin/banks/${b.id}/toggle`, {}); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const remove = async (b) => {
    if (!window.confirm(`حذف بنك "${b.bank_name}" نهائياً؟`)) return;
    setBusy(true);
    try { await api.delete(`/admin/banks/${b.id}`); await load(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div dir={dir} style={css.page} data-testid="banks-admin-page">
      <header style={css.head}>
        <button onClick={() => navigate("/")} style={css.backBtn} data-testid="banks-back">
          <BackIcon size={14} /> رجوع
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={css.title}>البنوك المعتمدة</h1>
          <p style={css.sub}>يستخدمها العميل في صفحة السداد ضمن قائمة منسدلة</p>
        </div>
        <button onClick={openNew} style={css.addBtn} data-testid="banks-add">
          <Plus size={14} strokeWidth={2.4} /> إضافة بنك
        </button>
      </header>

      {err && <div style={css.err} data-testid="banks-error"><AlertCircle size={14} /> {err}</div>}

      <main style={css.list}>
        {banks.length === 0 ? (
          <div style={css.empty} data-testid="banks-empty">
            <Building2 size={36} strokeWidth={1.5} style={{ color: theme.textMuted, marginBottom: 8 }} />
            <p style={{ color: theme.textMuted, fontSize: 13 }}>لا توجد بنوك مضافة بعد. ابدأ بإضافة الراجحي مثلاً.</p>
          </div>
        ) : banks.map((b) => (
          <article key={b.id} style={{ ...css.card, opacity: b.is_enabled ? 1 : 0.55 }} data-testid={`bank-row-${b.id}`}>
            <div style={css.cardHead}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {b.logo_url
                  ? <div style={{
                      width: 36, height: 36, borderRadius: "50%",
                      background: theme.bg, border: `1.5px solid ${theme.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      overflow: "hidden", flexShrink: 0,
                    }}>
                      <img src={resolveLogo(b.logo_url)} alt={b.bank_name}
                        style={{ width: "78%", height: "78%", objectFit: "contain" }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    </div>
                  : <Building2 size={20} strokeWidth={2.2} style={{ color: theme.gold }} />}
                <div>
                  <div style={{ fontWeight: 900, fontSize: 14 }}>{b.bank_name}</div>
                  {b.bank_code && <div style={{ fontSize: 10, color: theme.textMuted, fontFamily: "monospace" }}>{b.bank_code}</div>}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => toggle(b)} title={b.is_enabled ? "تعطيل" : "تفعيل"} disabled={busy}
                  style={{ ...css.iconBtn, color: b.is_enabled ? "#16a34a" : "#dc2626" }}
                  data-testid={`bank-toggle-${b.id}`}>
                  {b.is_enabled ? <Power size={14} /> : <PowerOff size={14} />}
                </button>
                <button onClick={() => openEdit(b)} title="تعديل" style={{ ...css.iconBtn, color: theme.gold }} data-testid={`bank-edit-${b.id}`}>
                  <Pencil size={14} />
                </button>
                <button onClick={() => remove(b)} title="حذف" style={{ ...css.iconBtn, color: "#dc2626" }} data-testid={`bank-delete-${b.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <div style={css.cardBody}>
              <Row label="اسم الحساب" value={b.account_name} theme={theme} />
              <Row label="رقم الحساب" value={b.account_number} mono theme={theme} />
              <Row label="IBAN" value={b.iban} mono theme={theme} />
              {b.swift && <Row label="SWIFT" value={b.swift} mono theme={theme} />}
            </div>
          </article>
        ))}
      </main>

      {editing && (
        <div role="dialog" style={css.modalOverlay} onClick={close} data-testid="banks-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} style={css.modal}>
            <div style={css.modalHead}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 900 }}>
                {editing === "new" ? "إضافة بنك جديد" : `تعديل ${editing.bank_name}`}
              </h2>
              <button type="button" onClick={close} style={css.closeBtn}><X size={16} /></button>
            </div>
            <div style={css.formGrid}>
              <Field label="اسم البنك *" theme={theme}>
                <input required value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                  placeholder="مثال: مصرف الراجحي" style={css.input} data-testid="bank-form-name" />
              </Field>
              <Field label="رمز البنك" theme={theme}>
                <input value={form.bank_code} onChange={(e) => setForm({ ...form, bank_code: e.target.value })}
                  placeholder="ALRAJHI / ALINMA / STC" style={css.input} data-testid="bank-form-code" />
              </Field>
              <Field label="اسم صاحب الحساب *" theme={theme}>
                <input required value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })}
                  style={css.input} data-testid="bank-form-account-name" />
              </Field>
              <Field label="رقم الحساب *" theme={theme}>
                <input required dir="ltr" value={form.account_number}
                  onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                  style={{ ...css.input, fontFamily: "monospace" }} data-testid="bank-form-account-number" />
              </Field>
              <Field label="IBAN *" theme={theme}>
                <input required dir="ltr" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })}
                  placeholder="SA00..." style={{ ...css.input, fontFamily: "monospace" }} data-testid="bank-form-iban" />
              </Field>
              <Field label="SWIFT (اختياري)" theme={theme}>
                <input dir="ltr" value={form.swift} onChange={(e) => setForm({ ...form, swift: e.target.value })}
                  style={{ ...css.input, fontFamily: "monospace" }} data-testid="bank-form-swift" />
              </Field>
              <Field label="شعار البنك (اختياري — صورة من جهازك)" theme={theme} full>
                <BankLogoUploader
                  currentUrl={form.logo_url}
                  preview={logoPreview}
                  onPick={onPickLogo}
                  onRemove={editing !== "new" ? removeLogo : () => { setLogoFile(null); setLogoPreview(""); }}
                  theme={theme}
                />
              </Field>
              <Field label="ترتيب العرض" theme={theme}>
                <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                  style={css.input} data-testid="bank-form-order" />
              </Field>
              <Field label="تعليمات للعميل (عربي)" theme={theme} full>
                <textarea rows={2} value={form.instructions_ar} onChange={(e) => setForm({ ...form, instructions_ar: e.target.value })}
                  style={{ ...css.input, resize: "vertical" }} data-testid="bank-form-inst-ar" />
              </Field>
              <Field label="تعليمات للعميل (English)" theme={theme} full>
                <textarea rows={2} value={form.instructions_en} onChange={(e) => setForm({ ...form, instructions_en: e.target.value })}
                  style={{ ...css.input, resize: "vertical" }} data-testid="bank-form-inst-en" />
              </Field>
              <Field label="مفعّل" theme={theme}>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: theme.text }}>
                  <input type="checkbox" checked={form.is_enabled} onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })}
                    data-testid="bank-form-enabled" />
                  {form.is_enabled ? <CheckCircle2 size={14} style={{ color: "#16a34a" }} /> : null}
                </label>
              </Field>
            </div>
            <div style={css.modalFooter}>
              <button type="button" onClick={close} style={css.cancelBtn}>إلغاء</button>
              <button type="submit" disabled={busy} style={css.saveBtn} data-testid="bank-form-save">
                <Save size={14} strokeWidth={2.4} /> حفظ
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function BankLogoUploader({ currentUrl, preview, onPick, onRemove, theme }) {
  const ref = useRef(null);
  // Priority: local preview of just-picked file > saved-URL > placeholder.
  const shown = preview || (currentUrl ? resolveLogo(currentUrl) : "");

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      padding: 10, border: `1px dashed ${theme.border}`, borderRadius: 10,
      background: theme.surface,
    }} data-testid="bank-logo-uploader">
      <div style={{
        width: 72, height: 72, borderRadius: "50%", background: theme.bg,
        border: `2px solid ${theme.border}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", flexShrink: 0,
        boxShadow: shown ? "0 2px 8px rgba(212,175,55,0.18)" : "none",
      }}>
        {shown ? (
          <img src={shown} alt="logo preview"
            style={{ width: "82%", height: "82%", objectFit: "contain", display: "block" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            data-testid="bank-logo-preview" />
        ) : (
          <Building2 size={28} strokeWidth={1.8} style={{ color: theme.textMuted }} />
        )}
      </div>
      <div style={{ display: "flex", gap: 8, flex: 1, flexWrap: "wrap" }}>
        <input
          ref={ref}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          style={{ display: "none" }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); }}
          data-testid="bank-logo-file"
        />
        <button type="button" onClick={() => ref.current?.click()}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: theme.gold, color: "#0a0a0a", border: "none",
            padding: "8px 14px", borderRadius: 8, fontWeight: 800,
            fontSize: 12, cursor: "pointer",
          }}
          data-testid="bank-logo-upload">
          <Upload size={14} strokeWidth={2.4} />
          {shown ? "استبدال الشعار" : "اختيار شعار"}
        </button>
        {shown && (
          <button type="button" onClick={onRemove}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              background: "transparent", color: "#dc2626",
              border: "1px solid #dc2626",
              padding: "8px 12px", borderRadius: 8, fontWeight: 700,
              fontSize: 12, cursor: "pointer",
            }}
            data-testid="bank-logo-remove">
            <Trash2 size={12} /> حذف
          </button>
        )}
        <div style={{ flexBasis: "100%", fontSize: 10, color: theme.textMuted, marginTop: 2 }}>
          PNG / JPG / WEBP / SVG — حتى 2 ميجا
        </div>
      </div>
    </div>
  );
}


function Row({ label, value, mono, theme }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", padding: "5px 0",
      borderBottom: `1px dashed ${theme.borderSoft}`, fontSize: 12,
    }}>
      <span style={{ color: theme.textMuted, fontWeight: 700 }}>{label}</span>
      <span dir={mono ? "ltr" : undefined} style={{ fontFamily: mono ? "monospace" : "inherit", color: theme.text, fontWeight: 700 }}>{value || "—"}</span>
    </div>
  );
}

function Field({ label, children, theme, full }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : "auto" }}>
      <div style={{ fontSize: 11, color: theme.textMuted, fontWeight: 700, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function makeStyles(theme) {
  return {
    page: { padding: 18, background: theme.bg, color: theme.text, minHeight: "100vh" },
    head: { display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" },
    title: { margin: 0, fontSize: 22, fontWeight: 900 },
    sub: { margin: 0, fontSize: 12, color: theme.textMuted },
    backBtn: {
      background: "transparent", border: `1px solid ${theme.border}`, color: theme.text,
      padding: "6px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 6,
    },
    addBtn: {
      background: theme.gold, border: "none", color: "#0a0a0a",
      padding: "8px 14px", borderRadius: 10, fontSize: 13, fontWeight: 900, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 6,
    },
    err: {
      background: "rgba(220,38,38,0.10)", color: "#dc2626", padding: "10px 12px",
      borderRadius: 10, fontSize: 13, fontWeight: 700, marginBottom: 12,
      display: "inline-flex", gap: 8, alignItems: "center",
    },
    list: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 },
    empty: {
      gridColumn: "1 / -1", textAlign: "center", padding: 36,
      background: theme.surface, borderRadius: 12, border: `1px dashed ${theme.border}`,
    },
    card: {
      background: theme.surface, border: `1px solid ${theme.border}`,
      borderRadius: 12, padding: 14,
    },
    cardHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 },
    cardBody: { display: "flex", flexDirection: "column", gap: 2 },
    iconBtn: { background: "transparent", border: `1px solid ${theme.border}`, padding: 6, borderRadius: 8, cursor: "pointer", display: "inline-flex" },
    modalOverlay: {
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16, zIndex: 1000,
    },
    modal: {
      background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 16,
      padding: 20, width: "100%", maxWidth: 640, maxHeight: "92vh", overflowY: "auto",
    },
    modalHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 },
    closeBtn: { background: "transparent", border: "none", cursor: "pointer", color: theme.textMuted },
    formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 },
    input: {
      width: "100%", padding: "8px 10px", fontSize: 13, fontFamily: "inherit",
      background: theme.surface, color: theme.text, border: `1px solid ${theme.border}`,
      borderRadius: 8, outline: "none", boxSizing: "border-box",
    },
    modalFooter: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 },
    cancelBtn: {
      background: "transparent", border: `1px solid ${theme.border}`, color: theme.text,
      padding: "8px 14px", borderRadius: 10, fontWeight: 700, fontSize: 13, cursor: "pointer",
    },
    saveBtn: {
      background: theme.gold, color: "#0a0a0a", border: "none",
      padding: "8px 14px", borderRadius: 10, fontWeight: 900, fontSize: 13, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 6,
    },
  };
}
